
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useTranslation } from 'react-i18next';
import { DisplayDevicesSection } from '../../pages/home/components/DisplayDevicesSection';

interface ServiceItem {
  id: string;
  label: string;
  path: string;
  external?: boolean;
  modal?: 'display-devices-rules';
}

interface ServiceCategory {
  title: string;
  icon: string;
  color: string;
  services: ServiceItem[];
}

const serviceCategories: ServiceCategory[] = [
  {
    title: 'services.cat.bp.title',
    icon: 'ri-building-2-line',
    color: '#10B981',
    services: [
      { id: 'bp-1', label: 'services.item.bp-1', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'bp-2', label: 'services.item.bp-2', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'bp-3', label: 'services.item.bp-3', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'bp-4', label: 'services.item.bp-4', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'bp-5', label: 'services.item.bp-5', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
    ],
  },
  {
    title: 'services.cat.la.title',
    icon: 'ri-layout-grid-line',
    color: '#3B82F6',
    services: [
      { id: 'la-1', label: 'services.item.la-1', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'la-2', label: 'services.item.la-2', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'la-3', label: 'services.item.la-3', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
    ],
  },
  {
    title: 'services.cat.lrs.title',
    icon: 'ri-file-list-3-line',
    color: '#F59E0B',
    services: [
      // Filed and processed on the LRS portal, not here. This ERP only keeps
      // the books from the data that portal pushes to us, so every action a
      // citizen takes belongs there.
      { id: 'lrs-1', label: 'services.item.lrs-1', path: 'https://lrsdtcp.ap.gov.in/', external: true },
      { id: 'lrs-2', label: 'services.item.lrs-2', path: 'https://lrsdtcp.ap.gov.in/', external: true },
      { id: 'lrs-3', label: 'services.item.lrs-3', path: 'https://lrsdtcp.ap.gov.in/LRSCalculator', external: true },
      { id: 'lrs-4', label: 'services.item.lrs-4', path: 'https://lrsdtcp.ap.gov.in/', external: true },
      { id: 'lrs-5', label: 'services.item.lrs-5', path: '/services/lrs' },
    ],
  },
  {
    title: 'services.cat.bps.title',
    icon: 'ri-shield-check-line',
    color: '#8B5CF6',
    services: [
      // As LRS above — the BPS portal owns the application lifecycle.
      { id: 'bps-1', label: 'services.item.bps-1', path: 'https://bps.ap.gov.in', external: true },
      { id: 'bps-2', label: 'services.item.bps-2', path: 'https://bps.ap.gov.in', external: true },
      { id: 'bps-3', label: 'services.item.bps-3', path: 'https://bps.ap.gov.in', external: true },
      { id: 'bps-4', label: 'services.item.bps-4', path: 'https://bps.ap.gov.in', external: true },
      { id: 'bps-5', label: 'services.item.bps-5', path: '/services/bps' },
    ],
  },
  {
    title: 'services.cat.clu.title',
    icon: 'ri-landscape-line',
    color: '#EC4899',
    services: [
      { id: 'clu-1', label: 'services.item.clu-1', path: 'https://www.apindustries.gov.in/APIndus/UserInterface/SingleWindowServicesApplication/LoginPortal/Registration_ChangeOfLandUse.aspx', external: true },
      { id: 'clu-2', label: 'services.item.clu-2', path: 'https://www.apindustries.gov.in/APIndus/UserInterface/SingleWindowServicesApplication/LoginPortal/Registration_ChangeOfLandUse.aspx', external: true },
      { id: 'clu-3', label: 'services.item.clu-3', path: 'https://www.apindustries.gov.in/APIndus/UserInterface/SingleWindowServicesApplication/LoginPortal/Registration_ChangeOfLandUse.aspx', external: true },
    ],
  },
  {
    title: 'services.cat.mp.title',
    icon: 'ri-map-2-line',
    color: '#14B8A6',
    services: [
      { id: 'mp-1', label: 'services.item.mp-1', path: '/gis-map' },
      { id: 'mp-2', label: 'services.item.mp-2', path: '/gis-map' },
      { id: 'mp-3', label: 'services.item.mp-3', path: '/gis-map' },
    ],
  },
  {
    title: 'services.cat.tdr.title',
    icon: 'ri-exchange-line',
    color: '#F43F5E',
    services: [
      { id: 'tdr-1', label: 'services.item.tdr-1', path: 'https://tdrdtcp.apcfss.in/login.do', external: true },
      { id: 'tdr-2', label: 'services.item.tdr-2', path: 'https://tdrdtcp.apcfss.in/login.do', external: true },
      { id: 'tdr-3', label: 'services.item.tdr-3', path: 'https://tdrdtcp.apcfss.in/login.do', external: true },
    ],
  },
  {
    title: 'services.cat.dd.title',
    icon: 'ri-tv-2-line',
    color: '#E66A1A',
    services: [
      // Display Devices items use a hash-prefix to encode the post-login redirect.
      // The click handler stashes everything after `#vendor:` into sessionStorage
      // and does a full-page reload to the bare /app/vendor-portal/login URL —
      // that's the same URL the working "Vendor Login" header button uses, so
      // we know it resolves correctly. Login.tsx then reads sessionStorage.
      { id: 'dd-1', label: 'services.item.dd-1', path: '/app/vendor-portal/login' },
      { id: 'dd-2b', label: 'services.item.dd-2b', path: '/app/vendor-portal/login' },
      { id: 'dd-3', label: 'services.item.dd-3', path: '/app/vendor-portal/login#vendor:/app/maud-erp/display-devices/workflow' },
      { id: 'dd-4', label: 'services.item.dd-4', path: '/app/vendor-portal/login#vendor:/app/maud-erp/display-devices/demand-collection' },
      { id: 'dd-5', label: 'services.item.dd-5', path: '/app/vendor-portal/login#vendor:/app/maud-erp/display-devices' },
      { id: 'dd-6', label: 'services.item.dd-6', path: '#rules', modal: 'display-devices-rules' },
    ],
  },
  {
    title: 'services.cat.tt.title',
    icon: 'ri-signal-tower-line',
    color: '#06B6D4',
    services: [
      { id: 'tt-1', label: 'services.item.tt-1', path: 'https://ttdtcp.ap.gov.in/TT/', external: true },
      { id: 'tt-2', label: 'services.item.tt-2', path: 'https://ttdtcp.ap.gov.in/TT/', external: true },
    ],
  },
  {
    title: 'services.cat.st.title',
    icon: 'ri-community-line',
    color: '#6366F1',
    services: [
      { id: 'st-1', label: 'services.item.st-1', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'st-2', label: 'services.item.st-2', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
    ],
  },
  {
    title: 'services.cat.uda.title',
    icon: 'ri-government-line',
    color: '#F97316',
    services: [
      { id: 'uda-1', label: 'services.item.uda-1', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'uda-2', label: 'services.item.uda-2', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'uda-3', label: 'services.item.uda-3', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
      { id: 'uda-4', label: 'services.item.uda-4', path: 'https://portal.apdpms.ap.gov.in/Home.aspx', external: true },
    ],
  },
];

export const ServicesSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(serviceCategories.map(c => c.title)));
  const [activePanel, setActivePanel] = useState<ServiceItem['modal'] | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // When a service opens its inline panel, scroll the content into view so
  // the user isn't left looking at the grid when the page grows below.
  useEffect(() => {
    if (activePanel && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [activePanel]);

  const toggleCategory = (title: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedCategories(new Set(serviceCategories.map(c => c.title)));
  };

  const collapseAll = () => {
    setExpandedCategories(new Set());
  };

  // Filter categories and services by search term (compares against the
  // translated text so search works regardless of active language).
  const filteredCategories = searchTerm
    ? serviceCategories
        .map(cat => ({
          ...cat,
          services: cat.services.filter(s =>
            t(s.label).toLowerCase().includes(searchTerm.toLowerCase()) ||
            t(cat.title).toLowerCase().includes(searchTerm.toLowerCase())
          ),
        }))
        .filter(cat => cat.services.length > 0)
    : serviceCategories;

  // Auto-expand all when searching
  const effectiveExpanded = searchTerm
    ? new Set(filteredCategories.map(c => c.title))
    : expandedCategories;

  return (
    <section id="services" className="py-20 bg-stone-50 dark:bg-gray-900 section-pattern scroll-mt-36">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-12 scroll-animate ${headerVisible ? 'visible' : ''}`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-stone-800 dark:text-gray-100 mb-6 relative inline-block group">
            <span className="relative z-10">{t('services.title')}</span>
            <span className="absolute bottom-0 left-0 w-0 h-1 bg-amber-600 transition-all duration-500 group-hover:w-full"></span>
          </h2>
          <p className="text-xl text-stone-600 dark:text-gray-400 max-w-3xl mx-auto mb-8 transition-opacity duration-300">
            {t('services.description')}
          </p>

          {/* Search */}
          <div className="max-w-md mx-auto mb-4">
            <div className="relative">
              <i className="ri-search-line w-5 h-5 flex items-center justify-center absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400 dark:text-gray-500"></i>
              <input
                type="text"
                placeholder={t('services.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-stone-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-stone-800 dark:text-gray-200 placeholder-stone-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Expand/Collapse buttons */}
          {!searchTerm && (
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={expandAll}
                className="text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium transition-colors"
              >
                {t('services.expandAll')}
              </button>
              <span className="text-stone-300 dark:text-gray-600">|</span>
              <button
                onClick={collapseAll}
                className="text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 font-medium transition-colors"
              >
                {t('services.collapseAll')}
              </button>
            </div>
          )}
        </div>

        {/* Service Categories */}
        <div
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className={`grid md:grid-cols-2 lg:grid-cols-3 gap-5 scroll-animate ${gridVisible ? 'visible' : ''}`}
        >
          {filteredCategories.map((category) => {
            const isExpanded = effectiveExpanded.has(category.title);
            return (
              <div
                key={category.title}
                className="bg-white dark:bg-gray-800 rounded-xl border border-stone-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden"
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.title)}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-stone-50 dark:hover:bg-gray-700 transition-colors group"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
                    style={{ backgroundColor: `${category.color}18` }}
                  >
                    <i
                      className={`${category.icon} text-lg`}
                      style={{ color: category.color }}
                    ></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-stone-800 dark:text-gray-100 truncate">
                      {t(category.title)}
                    </h3>
                    <p className="text-xs text-stone-500 dark:text-gray-400">
                      {category.services.length} {t(category.services.length === 1 ? 'services.serviceSingular' : 'services.servicePlural')}
                    </p>
                  </div>
                  <i
                    className={`ri-arrow-down-s-line text-lg text-stone-400 dark:text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  ></i>
                </button>

                {/* Service Items */}
                {isExpanded && (
                  <div className="border-t border-stone-100 dark:border-gray-700">
                    {category.services.map((service, idx) => (
                      <button
                        key={service.id}
                        onClick={() => {
                          if (service.modal) { setActivePanel(service.modal); return; }
                          if (service.external) {
                            window.open(service.path, '_blank', 'noopener,noreferrer');
                            return;
                          }
                          // Encoded `?redirect=...` in the URL was being eaten by
                          // the global axios interceptor before vendor login
                          // could mount. Hash-encode the post-login target into
                          // sessionStorage and full-reload to the bare login URL
                          // (same URL the working "Vendor Login" header button
                          // uses), then have Login.tsx read sessionStorage.
                          const hashIdx = service.path.indexOf('#vendor:');
                          if (hashIdx >= 0) {
                            const target = service.path.substring(hashIdx + '#vendor:'.length);
                            sessionStorage.setItem('vendor_post_login_redirect', target);
                            window.location.assign(service.path.substring(0, hashIdx));
                            return;
                          }
                          if (service.path.startsWith('/app/')) {
                            window.location.assign(service.path);
                          } else {
                            navigate(service.path);
                          }
                        }}
                        className={`w-full flex items-center gap-3 px-5 py-3 text-left hover:bg-amber-50 dark:hover:bg-gray-700 transition-colors group ${
                          idx < category.services.length - 1
                            ? 'border-b border-stone-100 dark:border-gray-700/50'
                            : ''
                        }`}
                      >
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${category.color}12` }}
                        >
                          <i
                            className="ri-arrow-right-s-line text-xs"
                            style={{ color: category.color }}
                          ></i>
                        </div>
                        <span className="text-sm text-stone-700 dark:text-gray-300 flex-1 leading-snug group-hover:text-amber-700 dark:group-hover:text-amber-400 transition-colors">
                          {t(service.label)}
                        </span>
                        <i className={`${service.external ? 'ri-external-link-line' : 'ri-arrow-right-s-line'} text-stone-300 dark:text-gray-600 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors text-sm flex-shrink-0`}></i>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* No results */}
        {searchTerm && filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-search-line w-16 h-16 flex items-center justify-center text-stone-400 dark:text-gray-600 mx-auto mb-4"></i>
            <h3 className="text-xl font-semibold text-stone-600 dark:text-gray-400 mb-2">{t('services.noResults')}</h3>
            <p className="text-stone-500 dark:text-gray-500">{t('services.adjustSearch')}</p>
          </div>
        )}

        {activePanel === 'display-devices-rules' && (
          <div
            ref={panelRef}
            className="mt-10 overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800"
          >
            <div className="flex items-center justify-between gap-3 border-b border-stone-200 px-5 py-3 dark:border-gray-700">
              <div className="flex items-center gap-2 min-w-0">
                <i className="ri-tv-2-line text-lg text-[#E66A1A]" />
                <h3 className="text-sm font-semibold text-stone-800 dark:text-gray-100 truncate">
                  {t('services.ddRulesPanelTitle')}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setActivePanel(null)}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-stone-600 hover:bg-stone-100 hover:text-stone-900 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <i className="ri-close-line text-base" /> {t('services.close')}
              </button>
            </div>
            <DisplayDevicesSection />
          </div>
        )}
      </div>
    </section>
  );
};
